import { useEffect, useState } from 'react'
import type { JSX } from 'react'
import { THEME_STORAGE_KEY, preferredTheme } from '@/theme'
import type { Theme } from '@/theme'

/**
 * Switches between the two colourways.
 *
 * The token layer keys dark off `[data-theme="dark"]` on the root element (see
 * design/tokens/tailwind.config.ts), so every colour follows from this one
 * attribute and no component needs a dark-mode variant of its own.
 */
export function ThemeToggle(): JSX.Element {
  const [theme, setTheme] = useState<Theme>(() =>
    preferredTheme(window.localStorage, window.matchMedia('(prefers-color-scheme: dark)').matches),
  )

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
    window.localStorage.setItem(THEME_STORAGE_KEY, theme)
  }, [theme])

  const next: Theme = theme === 'dark' ? 'light' : 'dark'

  return (
    <button
      type="button"
      className="ar-btn ar-btn--secondary ar-btn--sm"
      aria-label={`Switch to ${next} theme`}
      onClick={() => {
        setTheme(next)
      }}
    >
      <span aria-hidden="true">{theme === 'dark' ? '☾' : '☀'}</span>
      <span>{theme === 'dark' ? 'Dark' : 'Light'}</span>
    </button>
  )
}
